import { Component } from '@theme/component';

/**
 * Base class for GST preference components
 * Handles shared logic for localStorage, events, and cross-component sync
 *
 * @template {import('@theme/component').Refs} [T=import('@theme/component').Refs]
 * @extends {Component<T>}
 */
class LindyGSTBase extends Component {
  static STORAGE_KEY = 'lindy_gst_preference';

  /**
   * Event handler for external GST changes
   * @type {((event: Event) => void) | undefined}
   */
  handleExternalChange;

  /**
   * Save GST preference to localStorage
   * @param {string} option - The GST option ('inc' or 'ex')
   */
  savePreference(option) {
    try {
      localStorage.setItem(LindyGSTBase.STORAGE_KEY, option);
    } catch (error) {
      console.warn('Could not save GST preference to localStorage:', error);
    }
  }

  /**
   * Dispatch GST change event to sync across components
   * @param {string} option - The GST option ('inc' or 'ex')
   */
  dispatchChangeEvent(option) {
    const event = new CustomEvent('gst-change', {
      detail: { option, source: this },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
    
    window.dispatchEvent(new CustomEvent('gst-change', {
      detail: { option, source: this },
    }));
  }

  /**
   * Listen to GST change events from other components
   */
  listenToExternalChanges() {
    this.handleExternalChange = (event) => {
      const customEvent = /** @type {CustomEvent} */ (event);
      if (customEvent.detail?.source === this) return;
      
      const option = customEvent.detail?.option;
      if (option) {
        this.onExternalChange(option);
      }
    };
    
    window.addEventListener('gst-change', /** @type {EventListener} */ (this.handleExternalChange));
  }

  /**
   * Handle external GST change (must be implemented by subclass)
   * @param {string} _option - The GST option ('inc' or 'ex')
   */
  onExternalChange(_option) {
    throw new Error('onExternalChange must be implemented by subclass');
  }

  /**
   * Cleanup event listeners when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.handleExternalChange) {
      window.removeEventListener('gst-change', /** @type {EventListener} */ (this.handleExternalChange));
    }
  }

  /**
   * Get current GST preference from localStorage
   * @returns {string} The GST preference ('inc' or 'ex')
   */
  static getPreference() {
    try {
      return localStorage.getItem(LindyGSTBase.STORAGE_KEY) || 'inc';
    } catch (error) {
      console.warn('Could not get GST preference:', error);
      return 'inc';
    }
  }
}

/**
 * GST selector custom element that allows toggling between Inc. GST and Ex. GST
 * Stores user preference in localStorage
 *
 * @typedef {object} SelectorRefs
 * @property {HTMLInputElement[]} [gstOptions]
 *
 * @extends {LindyGSTBase<SelectorRefs>}
 */
export class LindyGSTSelector extends LindyGSTBase {
  /**
   * Initialize component when connected to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    this.loadPreference();
    this.bindEvents();
    this.listenToExternalChanges();
  }

  /**
   * Bind change events to radio input elements
   * @this {LindyGSTSelector & HTMLElement}
   */
  bindEvents() {
    const radioInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'));
    radioInputs.forEach((input) => {
      input.addEventListener('change', (e) => this.handleToggle(e));
    });
  }

  /**
   * Handle GST change from other components
   * @param {string} option - The GST option ('inc' or 'ex')
   */
  onExternalChange(option) {
    this.setActiveOption(option, true); // silent = true to avoid circular events
  }

  /**
   * Handle toggle event from radio inputs
   * @param {Event} event - The change event
   */
  handleToggle(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const selectedOption = input.getAttribute('data-option');

    if (selectedOption) {
      this.savePreference(selectedOption);
      this.dispatchChangeEvent(selectedOption);
    }
  }

  /**
   * Set active radio option based on GST preference
   * @param {string} option - The GST option ('inc' or 'ex')
   * @param {boolean} silent - If true, don't trigger change events
   * @this {LindyGSTSelector & HTMLElement}
   */
  setActiveOption(option, silent = false) {
    const radioInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'));

    // Temporarily remove event listeners if silent mode
    const handlers = new Map();
    if (silent) {
      radioInputs.forEach((input) => {
        const handler = input.onchange;
        if (handler) {
          handlers.set(input, handler);
          input.onchange = null;
        }
      });
    }

    radioInputs.forEach((input) => {
      if (input.getAttribute('data-option') === option) {
        input.checked = true;
      } else {
        input.checked = false;
      }
    });

    // Restore event listeners if silent mode
    if (silent) {
      handlers.forEach((handler, input) => {
        input.onchange = handler;
      });
    }
  }

  /**
   * Load GST preference from localStorage and set active option
   * @this {LindyGSTSelector & HTMLElement}
   */
  loadPreference() {
    try {
      const saved = localStorage.getItem(LindyGSTBase.STORAGE_KEY);
      const defaultOption = this.getAttribute('data-default') || 'inc';
      const option = saved || defaultOption;

      // Check if current state already matches what it should be
      const radioInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'));
      const currentChecked = Array.from(radioInputs).find(input => input.checked);
      const currentOption = currentChecked?.getAttribute('data-option');

      // Only update if state is incorrect (use silent mode to avoid triggering change events)
      if (currentOption !== option) {
        this.setActiveOption(option, true); // silent = true
      }
    } catch (error) {
      console.warn('Could not load GST preference from localStorage:', error);
      const defaultOption = this.getAttribute('data-default') || 'inc';
      this.setActiveOption(defaultOption, true); // silent = true
    }
  }
}

/**
 * GST toggle switch component (for header)
 * Toggle switch design that syncs with GST selector
 *
 * @extends {LindyGSTBase}
 */
export class LindyGSTToggle extends LindyGSTBase {
  /**
   * The toggle button element
   * @type {HTMLElement | null}
   */
  button;

  /**
   * Event handler for toggle clicks
   * @type {((event: Event) => void) | undefined}
   */
  handleToggle;

  /**
   * Initialize component when connected to DOM
   * @this {LindyGSTToggle & HTMLElement}
   */
  connectedCallback() {
    super.connectedCallback();

    this.button = /** @type {HTMLElement | null} */ (this.querySelector('.lindy-gst-toggle__switch'));
    
    if (!this.button) {
      console.warn('GST toggle button not found');
      return;
    }

    this.loadPreference();
    this.bindEvents();
    this.listenToExternalChanges();
  }

  /**
   * Cleanup event listeners when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.button && this.handleToggle) {
      this.button.removeEventListener('click', /** @type {EventListener} */ (this.handleToggle));
    }
  }

  /**
   * Bind click and keyboard events to toggle button
   */
  bindEvents() {
    if (!this.button) return;
    
    this.handleToggle = (event) => {
      event.preventDefault();
      if (!this.button) return;
      
      const currentActive = this.button.dataset.active === 'true';
      const newOption = currentActive ? 'ex' : 'inc';
      
      this.setActive(!currentActive);
      this.savePreference(newOption);
      this.dispatchChangeEvent(newOption);
    };

    this.button.addEventListener('click', /** @type {EventListener} */ (this.handleToggle));
    
    // Keyboard support
    this.button.addEventListener('keydown', (event) => {
      if ('key' in event && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        this.handleToggle?.(event);
      }
    });
  }

  /**
   * Handle GST change from other components
   * @param {string} option - The GST option ('inc' or 'ex')
   */
  onExternalChange(option) {
    this.setActive(option === 'inc');
  }

  /**
   * Set toggle button active state
   * @param {boolean} isActive - Whether Inc. GST is active
   */
  setActive(isActive) {
    if (!this.button) return;

    this.button.dataset.active = String(isActive);
    this.button.setAttribute('aria-pressed', String(isActive));
  }

  /**
   * Load GST preference from localStorage and set toggle state
   * @this {LindyGSTToggle & HTMLElement}
   */
  loadPreference() {
    if (!this.button) return;

    try {
      const saved = localStorage.getItem(LindyGSTBase.STORAGE_KEY);
      const defaultOption = this.getAttribute('data-default') || 'inc';
      const option = saved || defaultOption;
      const shouldBeActive = (option === 'inc');

      // Check if current state already matches what it should be
      const currentActive = this.button.dataset.active === 'true';

      // Only update if state is incorrect
      if (currentActive !== shouldBeActive) {
        this.setActive(shouldBeActive);
      }
    } catch (error) {
      console.warn('Could not load GST preference from localStorage:', error);
      const defaultOption = this.getAttribute('data-default') || 'inc';
      this.setActive(defaultOption === 'inc');
    }
  }
}

/**
 * GST Price Display Manager
 * Manages the visibility of BSS B2B price elements based on GST preference.
 * Listens to the 'gst-change' event from lindy-gst-toggle and lindy-gst-selector
 * components and shows/hides the appropriate price spans.
 *
 * BSS B2B injects two price spans:
 * - .bss-b2b-price-no-vat (ex. GST price)
 * - .bss-b2b-price-has-vat (inc. GST price)
 *
 * This component ensures only the selected price type is visible.
 */
class LindyGSTPriceDisplay {
  static STORAGE_KEY = 'lindy_gst_preference';

  /**
   * CSS class for BSS B2B ex. GST prices
   * @type {string}
   */
  static SELECTOR_NO_VAT = '.bss-b2b-price-no-vat';

  /**
   * CSS class for BSS B2B inc. GST prices
   * @type {string}
   */
  static SELECTOR_HAS_VAT = '.bss-b2b-price-has-vat';

  /**
   * MutationObserver instance for watching DOM changes
   * @type {MutationObserver | null}
   */
  observer = null;

  /**
   * Flag to prevent recursive updates during visibility changes
   * @type {boolean}
   */
  isUpdating = false;

  /**
   * Debounce timer for mutation observer updates
   * @type {number | null}
   */
  debounceTimer = null;

  constructor() {
    this.init();
  }

  /**
   * Initialize the price display manager
   */
  init() {
    // Listen for GST preference changes
    this.listenToGSTChanges();

    // Watch for dynamically added BSS B2B prices
    this.observeDOMChanges();

    // Apply initial state after a short delay to let BSS B2B inject prices first
    // BSS B2B typically runs on DOMContentLoaded, so we use requestAnimationFrame
    // to ensure we run after their initialization
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.applyPriceVisibility();
      });
    });
  }

  /**
   * Get current GST preference from localStorage
   * @returns {string} 'inc' or 'ex'
   */
  getCurrentPreference() {
    try {
      return localStorage.getItem(LindyGSTPriceDisplay.STORAGE_KEY) || 'inc';
    } catch (error) {
      console.warn('Could not get GST preference:', error);
      return 'inc';
    }
  }

  /**
   * Listen to gst-change events from toggle/selector components
   */
  listenToGSTChanges() {
    window.addEventListener('gst-change', (event) => {
      const customEvent = /** @type {CustomEvent} */ (event);
      const option = customEvent.detail?.option;

      if (option) {
        this.applyPriceVisibility(option);
      }
    });
  }

  /**
   * Apply price visibility based on GST preference
   * @param {string} [option] - GST option ('inc' or 'ex'). If not provided, reads from localStorage
   */
  applyPriceVisibility(option) {
    // Prevent recursive updates
    if (this.isUpdating) return;
    this.isUpdating = true;

    const preference = option || this.getCurrentPreference();

    // Update HTML class for CSS-based visibility (prevents FOUC)
    document.documentElement.classList.remove('gst-inc', 'gst-ex');
    document.documentElement.classList.add('gst-' + preference);

    // Reset flag after DOM updates settle
    requestAnimationFrame(() => {
      this.isUpdating = false;
    });
  }

  /**
   * Show elements by removing display: none
   * @param {NodeListOf<Element>} elements - Elements to show
   */
  showElements(elements) {
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = '';
      }
    });
  }

  /**
   * Hide elements by setting display: none
   * @param {NodeListOf<Element>} elements - Elements to hide
   */
  hideElements(elements) {
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = 'none';
      }
    });
  }

  /**
   * Watch for dynamically added BSS B2B prices and apply visibility
   * Note: CSS now handles visibility via html.gst-inc/html.gst-ex classes,
   * so this observer is no longer needed. Keeping the method structure
   * for potential future use but not observing DOM changes.
   */
  observeDOMChanges() {
    // CSS-based visibility via html.gst-inc/html.gst-ex classes handles
    // all price visibility automatically - no observer needed
  }

  /**
   * Cleanup observer when needed
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}

if (!customElements.get('lindy-gst-selector')) {
  customElements.define('lindy-gst-selector', LindyGSTSelector);
}

if (!customElements.get('lindy-gst-toggle')) {
  customElements.define('lindy-gst-toggle', LindyGSTToggle);
}

// Initialize price display manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.lindyGSTPriceDisplay = new LindyGSTPriceDisplay();
  });
} else {
  // DOM is already loaded
  window.lindyGSTPriceDisplay = new LindyGSTPriceDisplay();
}
